import {
  computeAddressSummary,
  computePraticeLocationSummary,
  DocumentBaseVariableId,
  getDistanceToNowInDays,
  HTMLTypeBaseVariableId,
  inputInseeNumberFormatter,
  MedicalEventBaseVariableId,
  ModuleDataBaseVariableId,
  NormalVariableDataJson,
  OtherBaseVariableId,
  PatientBaseVariableId,
  renderImage,
  RenderVariableDataJson,
  SlateTypeBaseVariableId,
  SlateVariableDataJson,
  TreatmentBaseVariableId,
  VariableDataSerializer,
  VariablesDataJson,
} from '@follow/cdk'
import { EditorValueJSON, getEditorEmptyJsonValue, JSONToValue } from '@follow/farte'
import { addDays } from 'date-fns'
import { Address } from '../../model/Address'
import { User } from '../../model/User'
import { documentVariablesDataSelector } from '../../store/domain/editor'
import {
  DATE_FORMAT_API,
  DATE_FORMAT_FRONT_SHORT,
  DATE_FORMAT_VERBOSE,
  formatFr,
} from '../date.utilities'
import { isDoctor } from '../user.utilities'
import { DocumentTemplate, FarteDocumentTemplate } from '../../model/DocumentTemplate'
import { RenderableDocumentTemplate } from '../../data/documentTemplates/mapper/documents.model'

const MOCK_ADDRESS: Address = {
  street: '1 Bd de la Liberté',
  street2: 'La liberté',
  city: 'Rennes',
  country: 'France',
  zipCode: '35000',
}

const getFakePatientBaseVariables = (): Record<PatientBaseVariableId, NormalVariableDataJson> => ({
  [PatientBaseVariableId.PATIENT_ADDRESS]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_ADDRESS,
    fallbackValue: null,
    value: computeAddressSummary(MOCK_ADDRESS, true) ?? '',
  },
  [PatientBaseVariableId.PATIENT_ADDRESS_LINEBREAKLESS]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_ADDRESS_LINEBREAKLESS,
    fallbackValue: null,
    value: computeAddressSummary(MOCK_ADDRESS, false) ?? '',
  },
  [PatientBaseVariableId.PATIENT_ADDRESS_SHORT]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_ADDRESS_SHORT,
    fallbackValue: null,
    value: `${MOCK_ADDRESS.street2} ${MOCK_ADDRESS.street}`,
  },
  [PatientBaseVariableId.PATIENT_ADDRESS_ZIPCODE]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_ADDRESS_ZIPCODE,
    fallbackValue: null,
    value: MOCK_ADDRESS.zipCode ?? '',
  },
  [PatientBaseVariableId.PATIENT_ADDRESS_CITY]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_ADDRESS_CITY,
    fallbackValue: null,
    value: MOCK_ADDRESS.city ?? '',
  },
  [PatientBaseVariableId.PATIENT_AGE]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_AGE,
    fallbackValue: null,
    value: '00',
  },
  [PatientBaseVariableId.PATIENT_AGE_WITH_UNIT]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_AGE_WITH_UNIT,
    fallbackValue: null,
    value: '00 ans',
  },
  [PatientBaseVariableId.HEALTH_INSURANCE]: {
    type: 'normal',
    id: PatientBaseVariableId.HEALTH_INSURANCE,
    fallbackValue: null,
    value: '021510516514658',
  },
  [PatientBaseVariableId.BIRTH_DATE]: {
    type: 'normal',
    id: PatientBaseVariableId.BIRTH_DATE,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [PatientBaseVariableId.BIRTH_DATE_NO_HASH]: {
    type: 'normal',
    id: PatientBaseVariableId.BIRTH_DATE_NO_HASH,
    fallbackValue: null,
    value: formatFr(new Date(), 'ddMMyyyy'),
  },
  [PatientBaseVariableId.EMAIL_ADDRESS]: {
    type: 'normal',
    id: PatientBaseVariableId.EMAIL_ADDRESS,
    fallbackValue: null,
    value: 'email@example.com',
  },
  [PatientBaseVariableId.SERIAL]: {
    type: 'normal',
    id: PatientBaseVariableId.SERIAL,
    fallbackValue: null,
    value: 'AAAAAA',
  },
  [PatientBaseVariableId.ATTACHED_DOCTOR]: {
    type: 'normal',
    id: PatientBaseVariableId.ATTACHED_DOCTOR,
    fallbackValue: null,
    value: 'Dr Médecin Traitant',
  },
  [PatientBaseVariableId.ADDRESSING_DOCTOR]: {
    type: 'normal',
    id: PatientBaseVariableId.ADDRESSING_DOCTOR,
    fallbackValue: null,
    value: 'Dr Adressant',
  },
  [PatientBaseVariableId.PATIENT_NAME]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_NAME,
    fallbackValue: null,
    value: 'Jannice JOHNATHAN',
  },
  [PatientBaseVariableId.PATIENT_USED_NAME]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_USED_NAME,
    fallbackValue: null,
    value: 'Jannice JOHNATHAN',
  },
  [PatientBaseVariableId.PATIENT_BIRTH_NAME]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_BIRTH_NAME,
    fallbackValue: null,
    value: 'Jane JOHN',
  },
  [PatientBaseVariableId.PATIENT_BIRTH_NAME_AND_USED_NAME]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_BIRTH_NAME_AND_USED_NAME,
    fallbackValue: null,
    value: 'Jannice JOHNATHAN (né.e Jane JOHN)',
  },
  [PatientBaseVariableId.PATIENT_FAMILYNAME]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_FAMILYNAME,
    fallbackValue: null,
    value: 'JOHNATHAN',
  },
  [PatientBaseVariableId.PATIENT_FIRSTNAME]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_FIRSTNAME,
    fallbackValue: null,
    value: 'Jannice',
  },
  [PatientBaseVariableId.PATIENT_BIRTH_FIRSTNAME]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_BIRTH_FIRSTNAME,
    fallbackValue: null,
    value: 'Jane',
  },
  [PatientBaseVariableId.PATIENT_BIRTH_LASTNAME]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_BIRTH_LASTNAME,
    fallbackValue: null,
    value: 'John',
  },
  [PatientBaseVariableId.PATIENT_USED_FIRSTNAME]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_USED_FIRSTNAME,
    fallbackValue: null,
    value: 'Jannice',
  },
  [PatientBaseVariableId.PATIENT_USED_LASTNAME]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_USED_LASTNAME,
    fallbackValue: null,
    value: 'Johnathan',
  },
  [PatientBaseVariableId.PATIENT_OTHER_FIRST_NAMES]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_OTHER_FIRST_NAMES,
    fallbackValue: null,
    value: 'Jaja Jiji',
  },
  [PatientBaseVariableId.SECU_NUMBER]: {
    type: 'normal',
    id: PatientBaseVariableId.SECU_NUMBER,
    fallbackValue: null,
    value: inputInseeNumberFormatter('299999999999945'),
  },
  [PatientBaseVariableId.SECU_NUMBER_REDUCED]: {
    type: 'normal',
    id: PatientBaseVariableId.SECU_NUMBER_REDUCED,
    fallbackValue: null,
    value: '2999999999999',
  },
  [PatientBaseVariableId.SECU_KEY]: {
    type: 'normal',
    id: PatientBaseVariableId.SECU_KEY,
    fallbackValue: null,
    value: '45',
  },
  [PatientBaseVariableId.PHONE_NUMBER1]: {
    type: 'normal',
    id: PatientBaseVariableId.PHONE_NUMBER1,
    fallbackValue: null,
    value: '+33 6 01 02 03 04',
  },
  [PatientBaseVariableId.PHONE_NUMBER1_TRIMMED]: {
    type: 'normal',
    id: PatientBaseVariableId.PHONE_NUMBER1_TRIMMED,
    fallbackValue: null,
    value: '+33601020304',
  },
  [PatientBaseVariableId.PROFESSION]: {
    type: 'normal',
    id: PatientBaseVariableId.PROFESSION,
    fallbackValue: null,
    value: 'Profession',
  },
  [PatientBaseVariableId.PHONE_NUMBER2]: {
    type: 'normal',
    id: PatientBaseVariableId.PHONE_NUMBER2,
    fallbackValue: null,
    value: '+33 6 05 06 07 08',
  },
  [PatientBaseVariableId.PHONE_NUMBER2_TRIMMED]: {
    type: 'normal',
    id: PatientBaseVariableId.PHONE_NUMBER2_TRIMMED,
    fallbackValue: null,
    value: '+33605060708',
  },
  [PatientBaseVariableId.PATIENT_CIVILITY]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_CIVILITY,
    fallbackValue: null,
    value: 'Madame',
  },
  [PatientBaseVariableId.PATIENT_CIVILITY_ABBREVIATION]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_CIVILITY_ABBREVIATION,
    fallbackValue: null,
    value: 'Mme',
  },
  [PatientBaseVariableId.PATIENT_BIRTH_PLACE_CODE]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_BIRTH_PLACE_CODE,
    fallbackValue: null,
    value: '35238',
  },
  [PatientBaseVariableId.PATIENT_BIRTH_PLACE_CODE_CITY]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_BIRTH_PLACE_CODE_CITY,
    fallbackValue: null,
    value: 'Rennes, France',
  },
  [PatientBaseVariableId.PATIENT_ASSIGNED_DOCTOR_ADDRESS]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_ASSIGNED_DOCTOR_ADDRESS,
    fallbackValue: null,
    value: computeAddressSummary(MOCK_ADDRESS) ?? '',
  },
  [PatientBaseVariableId.PATIENT_SEX]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_SEX,
    fallbackValue: null,
    value: 'Femme',
  },
  [PatientBaseVariableId.PATIENT_SEX_M_F_I]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_SEX_M_F_I,
    fallbackValue: null,
    value: 'Féminin',
  },
  [PatientBaseVariableId.E_IF_FEMALE]: {
    type: 'normal',
    id: PatientBaseVariableId.E_IF_FEMALE,
    fallbackValue: null,
    value: 'e',
  },
  [PatientBaseVariableId.THIRD_PRONOUN]: {
    type: 'normal',
    id: PatientBaseVariableId.THIRD_PRONOUN,
    fallbackValue: null,
    value: 'elle',
  },
  [PatientBaseVariableId.PERSONAL_PRONOUN]: {
    type: 'normal',
    id: PatientBaseVariableId.PERSONAL_PRONOUN,
    fallbackValue: null,
    value: 'la',
  },
  [PatientBaseVariableId.POSSESIVE_ADJECTIVE]: {
    type: 'normal',
    id: PatientBaseVariableId.POSSESIVE_ADJECTIVE,
    fallbackValue: null,
    value: 'sa',
  },
  [PatientBaseVariableId.OBJECT_PRONOUN]: {
    type: 'normal',
    id: PatientBaseVariableId.OBJECT_PRONOUN,
    fallbackValue: null,
    value: 'elle',
  },
  [PatientBaseVariableId.RECIPIENT_ASSIGNED_DOCTOR]: {
    type: 'normal',
    id: PatientBaseVariableId.RECIPIENT_ASSIGNED_DOCTOR,
    fallbackValue: null,
    value: 'Médecin traitant',
  },
  [PatientBaseVariableId.PATIENT_OID]: {
    type: 'normal',
    id: PatientBaseVariableId.PATIENT_OID,
    fallbackValue: null,
    value: 'INS-C',
  },
})

const getFakeDocumentBaseVariables = (): Record<
  DocumentBaseVariableId,
  NormalVariableDataJson
> => ({
  [DocumentBaseVariableId.DOCUMENT_CREATION_DATE]: {
    type: 'normal',
    id: DocumentBaseVariableId.DOCUMENT_CREATION_DATE,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_VERBOSE),
  },
  [DocumentBaseVariableId.DOCUMENT_CREATION_DATE_NO_HASH]: {
    type: 'normal',
    id: DocumentBaseVariableId.DOCUMENT_CREATION_DATE_NO_HASH,
    fallbackValue: null,
    value: formatFr(new Date(), 'ddMMyyyy'),
  },
  [DocumentBaseVariableId.DOCUMENT_REPLACE_NOTE]: {
    type: 'normal',
    id: DocumentBaseVariableId.DOCUMENT_REPLACE_NOTE,
    fallbackValue: null,
    value: 'ANNULE ET REMPLACE',
  },
  [DocumentBaseVariableId.DOCUMENT_NAME]: {
    type: 'normal',
    id: DocumentBaseVariableId.DOCUMENT_NAME,
    fallbackValue: null,
    value: 'Nom du document',
  },
  [DocumentBaseVariableId.DOCUMENT_START_GREETING]: {
    type: 'normal',
    id: DocumentBaseVariableId.DOCUMENT_START_GREETING,
    fallbackValue: null,
    value: 'Formule de politesse de début',
  },
  [DocumentBaseVariableId.DOCUMENT_END_GREETING]: {
    type: 'normal',
    id: DocumentBaseVariableId.DOCUMENT_END_GREETING,
    fallbackValue: null,
    value: 'Formule de politesse de fin',
  },
})

const getFakeTreatmentBaseVariables = (): Record<
  TreatmentBaseVariableId,
  NormalVariableDataJson
> => ({
  [TreatmentBaseVariableId.CURRENT_TREATMENTS]: {
    type: 'normal',
    id: TreatmentBaseVariableId.CURRENT_TREATMENTS,
    fallbackValue: null,
    value: 'Traitements en cours (tous)',
  },
  [TreatmentBaseVariableId.CURRENT_DOCTOR_TREATMENTS]: {
    type: 'normal',
    id: TreatmentBaseVariableId.CURRENT_DOCTOR_TREATMENTS,
    fallbackValue: null,
    value: 'Traitements en cours (mes prescriptions)',
  },
  [TreatmentBaseVariableId.CURRENT_EXTERNAL_TREATMENTS]: {
    type: 'normal',
    id: TreatmentBaseVariableId.CURRENT_EXTERNAL_TREATMENTS,
    fallbackValue: null,
    value: 'Traitements en cours (autres praticiens)',
  },
  [TreatmentBaseVariableId.PATIENT_ALLERGIES_AND_MEDICAL_HISTORY]: {
    type: 'normal',
    id: TreatmentBaseVariableId.PATIENT_ALLERGIES_AND_MEDICAL_HISTORY,
    fallbackValue: null,
    value: 'Allergies et antécédents du patient',
  },
  [TreatmentBaseVariableId.PATIENT_ALLERGIES]: {
    type: 'normal',
    id: TreatmentBaseVariableId.PATIENT_ALLERGIES,
    fallbackValue: null,
    value: 'Allergies du patient',
  },
  [TreatmentBaseVariableId.DRUG_COUNT]: {
    type: 'normal',
    id: TreatmentBaseVariableId.DRUG_COUNT,
    fallbackValue: null,
    value: '0 médicament prescrit',
  },
})

const getFakeMedicalEventBaseVariables = (
  owner: User,
): Record<MedicalEventBaseVariableId, NormalVariableDataJson> => {
  const isOwnerDoctor = isDoctor(owner)

  return {
    [MedicalEventBaseVariableId.MEDICAL_EVENT_DATE]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.MEDICAL_EVENT_DATE,
      fallbackValue: null,
      value: formatFr(new Date(), DATE_FORMAT_VERBOSE),
    },
    [MedicalEventBaseVariableId.MEDICAL_EVENT_DATE_NO_HASH]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.MEDICAL_EVENT_DATE_NO_HASH,
      fallbackValue: null,
      value: formatFr(new Date(), 'ddMMyyyy'),
    },
    [MedicalEventBaseVariableId.MEDICAL_EVENT_DATE_SHORT]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.MEDICAL_EVENT_DATE_SHORT,
      fallbackValue: null,
      value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
    },
    [MedicalEventBaseVariableId.MEDICAL_EVENT_DATE_DAY_NEXT]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.MEDICAL_EVENT_DATE_DAY_NEXT,
      fallbackValue: null,
      value: formatFr(addDays(new Date(), 1), DATE_FORMAT_VERBOSE),
    },
    [MedicalEventBaseVariableId.MEDICAL_EVENT_DATE_DAY_BEFORE]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.MEDICAL_EVENT_DATE_DAY_BEFORE,
      fallbackValue: null,
      value: formatFr(addDays(new Date(), -1), DATE_FORMAT_VERBOSE),
    },
    [MedicalEventBaseVariableId.MEDICAL_EVENT_DATE_DELAY]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.MEDICAL_EVENT_DATE_DELAY,
      fallbackValue: null,
      value: getDistanceToNowInDays(new Date()),
    },
    [MedicalEventBaseVariableId.MEDICAL_EVENT_TITLE]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.MEDICAL_EVENT_TITLE,
      fallbackValue: null,
      value: 'Évènement médical',
    },
    [MedicalEventBaseVariableId.DOCTOR_NAME]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.DOCTOR_NAME,
      fallbackValue: null,
      value: `${owner.firstName} ${owner.familyName.toUpperCase()}`,
    },
    [MedicalEventBaseVariableId.DOCTOR_TITLE]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.DOCTOR_TITLE,
      fallbackValue: null,
      value: (isOwnerDoctor && owner.profession?.description) || 'Profession',
    },
    [MedicalEventBaseVariableId.DOCTOR_QUALITY]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.DOCTOR_QUALITY,
      fallbackValue: null,
      value: (isOwnerDoctor && owner.quality) || 'Qualité',
    },
    [MedicalEventBaseVariableId.DOCTOR_SPECIALITY]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.DOCTOR_SPECIALITY,
      fallbackValue: null,
      value: (isOwnerDoctor && owner.speciality?.description) || 'Spécialité',
    },
    [MedicalEventBaseVariableId.DOCTOR_ACADEMIC_BACKGROUND_NO_LINE_BREAK]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.DOCTOR_ACADEMIC_BACKGROUND_NO_LINE_BREAK,
      fallbackValue: null,
      value:
        (isOwnerDoctor && owner.academicBackground?.replaceAll('\n', ' ')) || 'Parcours académique',
    },
    [MedicalEventBaseVariableId.DOCTOR_ACADEMIC_BACKGROUND_LINE_BREAK]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.DOCTOR_ACADEMIC_BACKGROUND_LINE_BREAK,
      fallbackValue: null,
      value: (isOwnerDoctor && owner.academicBackground) || 'Parcours académique',
    },
    [MedicalEventBaseVariableId.DOCTOR_ADDRESS]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.DOCTOR_ADDRESS,
      fallbackValue: null,
      value:
        computePraticeLocationSummary(
          isOwnerDoctor
            ? owner.practiceLocation
            : { name: 'Adresse du médecin', address: MOCK_ADDRESS },
        ) ?? '',
    },
    [MedicalEventBaseVariableId.DOCTOR_ADDRESS_LINEBREAKLESS]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.DOCTOR_ADDRESS_LINEBREAKLESS,
      fallbackValue: null,
      value:
        computePraticeLocationSummary(
          isOwnerDoctor
            ? owner.practiceLocation
            : { name: 'Adresse du médecin', address: MOCK_ADDRESS },
          false,
        ) ?? '',
    },
    [MedicalEventBaseVariableId.DOCTOR_EMAIL]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.DOCTOR_EMAIL,
      fallbackValue: null,
      value: owner.email,
    },
    [MedicalEventBaseVariableId.DOCTOR_EMAIL_MSS_MAILIZ]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.DOCTOR_EMAIL_MSS_MAILIZ,
      fallbackValue: null,
      value:
        isDoctor(owner) && owner.mailizEmailAddress
          ? owner.mailizEmailAddress
          : 'example@mssante.fr',
    },
    [MedicalEventBaseVariableId.RPPS_NUMBER]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.RPPS_NUMBER,
      fallbackValue: null,
      value: (isOwnerDoctor && owner.rppsNumber) || '99999999999',
    },
    [MedicalEventBaseVariableId.FINESS_NUMBER]: {
      type: 'normal',
      id: MedicalEventBaseVariableId.FINESS_NUMBER,
      fallbackValue: null,
      value: (isOwnerDoctor && owner.finessNumber) || '999999999',
    },
  }
}

const getFakeModuleDateBaseVariables = (): Record<
  ModuleDataBaseVariableId,
  NormalVariableDataJson
> => ({
  [ModuleDataBaseVariableId.PATIENT_BLOOD_GROUP]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_BLOOD_GROUP,
    fallbackValue: null,
    value: 'A positif (A+)',
  },
  [ModuleDataBaseVariableId.PATIENT_WEIGHT]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_WEIGHT,
    fallbackValue: null,
    value: '00 kg',
  },
  [ModuleDataBaseVariableId.PATIENT_SIZE]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_SIZE,
    fallbackValue: null,
    value: '000 cm',
  },
  [ModuleDataBaseVariableId.PATIENT_BMI]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_BMI,
    fallbackValue: null,
    value: '00.00',
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_GRAVIDITY]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_GRAVIDITY,
    fallbackValue: null,
    value: '00',
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_PARITY]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_PARITY,
    fallbackValue: null,
    value: '00',
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_NUMBER_OF_CHILDREN]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_NUMBER_OF_CHILDREN,
    fallbackValue: null,
    value: '0',
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_LAST_SMEAR]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_LAST_SMEAR,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_LAST_HIV_TEST]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_LAST_HIV_TEST,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_LAST_HPV_TEST]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_LAST_HPV_TEST,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_LAST_MAMMOGRAPHY]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_LAST_MAMMOGRAPHY,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_CONTRACEPTION_METHOD]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_CONTRACEPTION_METHOD,
    fallbackValue: null,
    value: 'Méthode de contraception',
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_PREGNANCY_TYPE]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_PREGNANCY_TYPE,
    fallbackValue: null,
    value: 'Type de grossesse',
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_TWIN_PREGNANCY]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_TWIN_PREGNANCY,
    fallbackValue: null,
    value: 'Type de grossesse gémellaire',
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_LAST_MENSTRUAL_PERIOD]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_LAST_MENSTRUAL_PERIOD,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_PREGNANCY_START]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_PREGNANCY_START,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_DUE_DATE]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_DUE_DATE,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_PATHOLOGICAL_LEAVE_START]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_PATHOLOGICAL_LEAVE_START,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_MATERNITY_LEAVE_START]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_MATERNITY_LEAVE_START,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_MATERNITY_LEAVE_END]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_MATERNITY_LEAVE_END,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [ModuleDataBaseVariableId.PATIENT_BLOOD_PRESSURE]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_BLOOD_PRESSURE,
    fallbackValue: null,
    value: '120',
  },
  [ModuleDataBaseVariableId.PATIENT_DIASTOLIC_PRESSURE]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_DIASTOLIC_PRESSURE,
    fallbackValue: null,
    value: '75',
  },
  [ModuleDataBaseVariableId.PATIENT_SYSTOLIC_PRESSURE]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_SYSTOLIC_PRESSURE,
    fallbackValue: null,
    value: '120/75',
  },
  [ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_AMENORRHEA]: {
    type: 'normal',
    id: ModuleDataBaseVariableId.PATIENT_GYNECOLOGICAL_AMENORRHEA,
    fallbackValue: null,
    value: '3 SA + 5 jours',
  },
})

const getfakeOtherBaseVariables = (): Record<OtherBaseVariableId, NormalVariableDataJson> => ({
  [OtherBaseVariableId.TODAY]: {
    type: 'normal',
    id: OtherBaseVariableId.TODAY,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_VERBOSE),
  },
  [OtherBaseVariableId.TODAY_COMPACT]: {
    type: 'normal',
    id: OtherBaseVariableId.TODAY_COMPACT,
    fallbackValue: null,
    value: formatFr(new Date(), DATE_FORMAT_FRONT_SHORT),
  },
  [OtherBaseVariableId.MENTIONS_LEGALES]: {
    type: 'normal',
    id: OtherBaseVariableId.MENTIONS_LEGALES,
    fallbackValue: null,
    value: `Votre praticien est engagé dans une démarche continue de protection des données de ses patients, conformément à la Loi Informatique et Liberté du 6 janvier 1978 et au Règlement européen sur la protection des données (RGPD)`,
  },
})

const MOCK_RPPS_BARCODE = `data:image/svg+xml;base64,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`
const MOCK_FINESS_BARCODE = `data:image/svg+xml;base64,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`
const MOCK_INS_BARCODE = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAG4AAABuCAIAAABJObGsAAABhGlDQ1BJQ0MgcHJvZmlsZQAAKJF9kT1Iw0AcxV9TiyKVDnYQcchQC4IFURFHqWIRLJS2QqsOJpd+QZOGJMXFUXAtOPixWHVwcdbVwVUQBD9AnB2cFF2kxP8lhRYxHhz34929x907QGhWmWr2TACqZhnpRFzM5VfF3lcICCCEMUQlZurJzGIWnuPrHj6+3sV4lve5P8eAUjAZ4BOJ55huWMQbxDObls55nzjMypJCfE48btAFiR+5Lrv8xrnksMAzw0Y2PU8cJhZLXSx3MSsbKvE0cURRNcoXci4rnLc4q9U6a9+TvzBY0FYyXKc5ggSWkEQKImTUUUEVFmK0aqSYSNN+3MM/7PhT5JLJVQEjxwJqUCE5fvA/+N2tWZyadJOCcSDwYtsfo0DvLtBq2Pb3sW23TgD/M3Cldfy1JjD7SXqjo0WOgNA2cHHd0eQ94HIHGHrSJUNyJD9NoVgE3s/om/LA4C3Qv+b21t7H6QOQpa6Wb4CDQyBaoux1j3f3dff275l2fz+QWXKz0nA31gAAAAlwSFlzAAAuIwAALiMBeKU/dgAAAAd0SU1FB+gCBQ8rHWX3RpAAAAAZdEVYdENvbW1lbnQAQ3JlYXRlZCB3aXRoIEdJTVBXgQ4XAAABcklEQVR42u3csW2DQBiA0RB5CAq3XoGRgJEMs8AC0DOBaViCtA6KAjZwtvD7ukiWTvd0P0iRfdE4jl/aom8EKFGi1Hzj7wKsWFXVuH8BNlIUxf2KTqUBR4lSqyk3eQM0TfNub9fnut1uTqUBR4lSASmjxb3znsuyXLiLPM+dSgOOUihRokQplChRohRKlIft9NCnj/FdrTRN0zR1Kg04Sm32rGzbdv0aXde9fJ+bbGQYhucpkyQ5xpEJsBEDjhLlB712qqrae8nL5RJgYwE2Esfx/Z+RH5sYcJQohRIlSpRCiRKlUKJEiVIoUaIUSpQoUQolSpRCiRIlSqFEiVIow1BG+1fXtVMplChRovyrw1xQ61SiRKk1PXZ3RlmWCz+5x+UUh6LMsgylAUeJEqVQokQplChRohTK17bXvzNQznS9XpEZcJQoPSun9X2/fo3/73X9FMrz+ey4GXCUKDV9VhZFsfeSk3tdD5MLag04SpSa7wcOoIz9dICHrwAAAABJRU5ErkJggg==`
const MOCK_SIGNATURE = `data:image/png;base64,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`
const getFakeHtmlBaseVariables = (): Record<HTMLTypeBaseVariableId, RenderVariableDataJson> => ({
  [HTMLTypeBaseVariableId.FINESS_BARCODE]: {
    type: 'render',
    id: HTMLTypeBaseVariableId.FINESS_BARCODE,
    fallbackValue: null,
    value: {
      renderer: 'DangerousHtml',
      props: {
        html: renderImage(MOCK_FINESS_BARCODE, `N° FINESS : 999999999`),
      },
      displayMode: 'block',
    },
  },
  [HTMLTypeBaseVariableId.RPPS_BARCODE]: {
    type: 'render',
    id: HTMLTypeBaseVariableId.RPPS_BARCODE,
    fallbackValue: null,
    value: {
      renderer: 'DangerousHtml',
      props: {
        html: renderImage(MOCK_RPPS_BARCODE, `N° RPPS : 99999999999`),
      },
      displayMode: 'block',
    },
  },
  [HTMLTypeBaseVariableId.PATIENT_INS_QRCODE]: {
    type: 'render',
    id: HTMLTypeBaseVariableId.PATIENT_INS_QRCODE,
    fallbackValue: null,
    value: {
      renderer: 'DangerousHtml',
      props: {
        html: renderImage(MOCK_INS_BARCODE, 'INS à scanner'),
      },
      displayMode: 'block',
    },
  },
  [HTMLTypeBaseVariableId.SIGNATURE]: {
    type: 'render',
    id: HTMLTypeBaseVariableId.SIGNATURE,
    fallbackValue: null,
    value: {
      renderer: 'DangerousHtml',
      props: {
        html: renderImage(MOCK_SIGNATURE),
      },
      displayMode: 'block',
    },
  },
  [HTMLTypeBaseVariableId.OBSERVATIONS]: {
    type: 'render',
    id: HTMLTypeBaseVariableId.OBSERVATIONS,
    fallbackValue: null,
    value: {
      renderer: 'DangerousHtml',
      props: {
        html: '<p>Observations</p>',
      },
      displayMode: 'block',
    },
  },
  [HTMLTypeBaseVariableId.RECIPIENTS]: {
    type: 'render',
    id: HTMLTypeBaseVariableId.RECIPIENTS,
    fallbackValue: null,
    value: {
      renderer: 'DangerousHtml',
      props: {
        html: '<p>Contacts</p>',
      },
      displayMode: 'block',
    },
  },
})

const getFakeSlateBaseVariables = (): Record<SlateTypeBaseVariableId, SlateVariableDataJson> => ({
  [SlateTypeBaseVariableId.NOTES]: {
    type: 'slate',
    id: SlateTypeBaseVariableId.NOTES,
    fallbackValue: null,
    value: getEditorEmptyJsonValue(undefined, undefined, 'Notes du patient'),
  },
})

/** Retourne un faux VariableData. Utilisé temporairemement pour les preview d'impression. https://fwhealth.atlassian.net/browse/DEV-3321 */
export const getFakeBaseVariables = (currentUser: User): VariablesDataJson => {
  return {
    ...getFakePatientBaseVariables(),
    ...getFakeDocumentBaseVariables(),
    ...getFakeTreatmentBaseVariables(),
    ...getFakeMedicalEventBaseVariables(currentUser),
    ...getFakeModuleDateBaseVariables(),
    ...getfakeOtherBaseVariables(),
    ...getFakeHtmlBaseVariables(),
    ...getFakeSlateBaseVariables(),
  }
}

/** Retourne un faux VariableData. Utilisé temporairemement pour les preview d'impression. https://fwhealth.atlassian.net/browse/DEV-3321 */
export const getFakeDocumentVariableData = (
  value: EditorValueJSON,
  document: FarteDocumentTemplate,
  user: User,
): VariablesDataJson => {
  const variables = VariableDataSerializer.serialize(
    documentVariablesDataSelector(JSONToValue(value), document),
  )
  return {
    ...variables,
    ...getFakeBaseVariables(user),
  }
}

export const generateFakeRenderDocument = (
  user: User,
  document: DocumentTemplate,
  layoutId?: number,
): RenderableDocumentTemplate => {
  const variablesData =
    document.type === 'farte'
      ? getFakeDocumentVariableData(document.template, document, user)
      : getFakeBaseVariables(user)

  return {
    type: document.type,
    category: document.category,
    variablesData,
    content: {
      documentId: document.id,
    },
    ...(layoutId && {
      layout: {
        documentLayoutId: layoutId,
      },
    }),
  }
}

export const generateFakeFileName = (
  renderableDocumentTemplate: RenderableDocumentTemplate,
): string => {
  const patientName = renderableDocumentTemplate.variablesData[PatientBaseVariableId.PATIENT_NAME]
    .value as string
  const cleanedName = patientName.replaceAll(' ', '-')
  const date = formatFr(new Date(), DATE_FORMAT_API)

  return `${cleanedName}-${date}-${document.title}.pdf`
}
